import { useState, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { chartData } from '../../data/mockData';
import { fetchCases, fetchClients } from '../../api';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import {
  Download,
  FileText,
  Image as ImageIcon,
  BarChart3,
  PieChartIcon,
  TrendingUp,
  Calendar,
  FileSpreadsheet,
  Package,
  Upload,
  Search,
  Check,
  CheckSquare,
  Square,
  FileDown,
  Info,
} from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 border border-slate-200 shadow-xl rounded-lg p-3 text-sm text-slate-750 backdrop-blur-md">
        <p className="text-slate-500 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="font-semibold">
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const hierarchyPerformance = [
  { role: 'Managers', total: 45, completed: 42, avgTime: '2.4 hrs', qcPassRate: '98%' },
  { role: 'Supreme Managers', total: 12, completed: 11, avgTime: '1.8 hrs', qcPassRate: '99%' },
  { role: 'Quality Checkers', total: 28, completed: 25, avgTime: '3.1 hrs', qcPassRate: '95%' },
  { role: 'Field Executives', total: 156, completed: 142, avgTime: '4.5 hrs', qcPassRate: '88%' },
  { role: 'Vendors', total: 84, completed: 76, avgTime: '5.2 hrs', qcPassRate: '82%' },
];

const EXPORT_COLUMNS = [
  { id: 'refNo', label: 'Ref No', category: 'Case Info' },
  { id: 'product', label: 'Product', category: 'Case Info' },
  { id: 'applicantName', label: 'Applicant Name', category: 'Applicant' },
  { id: 'contact', label: 'Contact', category: 'Applicant' },
  { id: 'address', label: 'Address', category: 'Applicant' },
  { id: 'priority', label: 'Priority', category: 'Case Info' },
  { id: 'pincode', label: 'Pincode', category: 'Region' },
  { id: 'city', label: 'City', category: 'Region' },
  { id: 'state', label: 'State', category: 'Region' },
  { id: 'assignDate', label: 'Assign Date', category: 'Timeline' },
  { id: 'assignTo', label: 'Assign To', category: 'Hierarchy' },
  { id: 'qcBy', label: 'QC By', category: 'Hierarchy' },
  { id: 'client', label: 'Client', category: 'Hierarchy' },
  { id: 'bp', label: 'BP (Business Partner)', category: 'Hierarchy' },
  { id: 'closeDate', label: 'Close Date', category: 'Timeline' },
  { id: 'penalty', label: 'Penalty Amount', category: 'Billing' },
  { id: 'status', label: 'Case Status', category: 'Case Info' },
  { id: 'feRemark', label: 'FE Remark', category: 'Remarks' },
  { id: 'finalRemark', label: 'Final QC Remark', category: 'Remarks' },
  { id: 'alternateContact', label: 'Alternate Contact', category: 'Applicant' },
  { id: 'companyName', label: 'Company Name', category: 'Applicant' },
];

export default function Reports() {
  const { currentRole } = useApp();
  const [activeTab, setActiveTab] = useState('analytics');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectedClient, setSelectedClient] = useState('all');
  const [caseSearchQuery, setCaseSearchQuery] = useState('');
  const [selectedCaseForReport, setSelectedCaseForReport] = useState(null);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const [data, clData] = await Promise.all([
          fetchCases().catch(() => []),
          fetchClients().catch(() => [])
        ]);
        if (Array.isArray(data)) setCases(data);
        if (Array.isArray(clData)) setClients(clData);
      } catch (err) {
        setCases([]);
        setClients([]);
      }
    }
    load();
  }, []);

  // Column Export Modal States
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('mis'); // mis, case, images
  const [selectedColumns, setSelectedColumns] = useState(EXPORT_COLUMNS.map(col => col.id));
  const [exportFormat, setExportFormat] = useState('xlsx'); // xlsx, csv, pdf
  const [columnSearch, setColumnSearch] = useState('');
  
  // Progress & Download Simulation
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Quick Select Helpers
  const handleSelectAll = () => {
    setSelectedColumns(EXPORT_COLUMNS.map(col => col.id));
    toast.info('All 21 columns selected for export.');
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
    toast.warning('All columns deselected. Select at least one.');
  };

  const toggleColumn = (colId) => {
    if (selectedColumns.includes(colId)) {
      setSelectedColumns(selectedColumns.filter(id => id !== colId));
    } else {
      setSelectedColumns([...selectedColumns, colId]);
    }
  };

  const triggerExportFlow = (type) => {
    setExportType(type);
    if (type === 'case') {
      setExportFormat('pdf');
    } else if (type === 'mis') {
      setExportFormat('xlsx');
    } else {
      setExportFormat('csv');
    }
    setShowExportModal(true);
  };

  // Simulating the actual file compilation and download progress
  const startFileCompilation = () => {
    if (selectedColumns.length === 0) {
      toast.error('Cannot generate report: Please select at least 1 column to export.');
      return;
    }

    setIsExporting(true);
    setExportProgress(0);
  };

  useEffect(() => {
    let interval;
    if (isExporting) {
      interval = setInterval(() => {
        setExportProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsExporting(false);
            setShowExportModal(false);
            
            // Format success alert
            const extension = exportFormat.toUpperCase();
            const filename = exportType === 'case' 
              ? `${selectedCaseForReport?.refNumber || 'CaseReport'}.${exportFormat}` 
              : exportType === 'mis' 
                ? `MIS_Export_${new Date().toISOString().split('T')[0]}.${exportFormat}`
                : `ImagePackage_Export.${exportFormat === 'pdf' ? 'pdf' : 'zip'}`;
                
            toast.success(`Success! Document compiled with ${selectedColumns.length} fields. Downloading ${filename}...`);
            return 100;
          }
          return prev + 10;
        });
      }, 150);
    }
    return () => clearInterval(interval);
  }, [isExporting, exportFormat, exportType, selectedColumns, selectedCaseForReport]);

  const filteredExportColumns = EXPORT_COLUMNS.filter(col => 
    col.label.toLowerCase().includes(columnSearch.toLowerCase()) ||
    col.category.toLowerCase().includes(columnSearch.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Reports & Analytical Insights</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Export custom SLA records, billing schedules, and compile verification statistics</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full text-slate-700">
        <TabsList className="bg-slate-100 border border-slate-200 p-0.5 rounded-lg flex inline-flex">
          <TabsTrigger value="analytics" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md text-slate-600 px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
            <BarChart3 className="w-4 h-4" />
            Performance Analytics
          </TabsTrigger>
          <TabsTrigger value="generate" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md text-slate-600 px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
            <FileText className="w-4 h-4" />
            Generate Custom Exports
          </TabsTrigger>
          <TabsTrigger value="downloads" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-md text-slate-600 px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
            <Download className="w-4 h-4" />
            Archive Downloads
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="mt-6 space-y-6">
          {/* Dashboard Filters Row */}
          <div className="flex items-center gap-4 bg-slate-50 p-3 rounded-xl border border-slate-200 backdrop-blur">
            {currentRole !== ROLES.CLIENT && (
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="w-48 bg-white border-slate-200 text-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
              <Calendar className="w-4 h-4 text-blue-500" />
              Interval:
            </div>
            <Input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-40 bg-white border-slate-200 text-slate-800 text-xs"
            />
            <span className="text-slate-500 text-xs font-semibold">to</span>
            <Input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-40 bg-white border-slate-200 text-slate-800 text-xs"
            />
          </div>

          {/* Recharts Graphical Visualization */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Case Volume Trends */}
            <div className="crm-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-bold text-slate-800">Turnaround Volume & SLA Trends</h3>
                <TrendingUp className="w-4 h-4 text-blue-500" />
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.caseTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="month" stroke="#475569" fontSize={11} />
                    <YAxis stroke="#475569" fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="cases" stroke="#6366f1" strokeWidth={2.5} dot={{ fill: '#6366f1', strokeWidth: 1.5 }} name="Allocated Leads" />
                    <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', strokeWidth: 1.5 }} name="Verified Passes" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Income Distribution (For Admin Roles Only) */}
            {currentRole === ROLES.ADMIN && (
              <div className="crm-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-base font-bold text-slate-800">Gross Billings vs Field Disbursements</h3>
                  <BarChart3 className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.revenueVsPayouts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                      <XAxis dataKey="month" stroke="#475569" fontSize={11} />
                      <YAxis stroke="#475569" fontSize={11} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} name="Client Billings" />
                      <Bar dataKey="payouts" fill="#10b981" radius={[4, 4, 0, 0]} name="Disbursed Payouts" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Status Segment Ratio */}
            <div className="crm-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-bold text-slate-800">QC Status Audit Distribution</h3>
                <PieChartIcon className="w-4 h-4 text-amber-400" />
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData.casesByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {chartData.casesByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Product Segment Ratio */}
            <div className="crm-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-bold text-slate-800">Volume distribution by Core Loan Product</h3>
                <Package className="w-4 h-4 text-purple-400" />
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.casesByProduct} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                    <XAxis type="number" stroke="#475569" fontSize={11} />
                    <YAxis dataKey="product" type="category" stroke="#475569" fontSize={11} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Cases Allocated" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Hierarchy Performance Tracking Panel */}
            <div className="crm-card p-6 col-span-1 lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-base font-bold text-slate-800">Management Hierarchy SLA Audits</h3>
                <TrendingUp className="w-4 h-4 text-blue-500" />
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-xs text-left text-slate-650">
                  <thead className="text-[10px] text-slate-600 uppercase bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Hierarchy Role</th>
                      <th className="px-4 py-3 font-semibold">Total Audited</th>
                      <th className="px-4 py-3 font-semibold">SLA Passes</th>
                      <th className="px-4 py-3 font-semibold">Mean Turnaround Time (TAT)</th>
                      <th className="px-4 py-3 font-semibold">QC Approval Ratio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {hierarchyPerformance.map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-semibold text-slate-800">{row.role}</td>
                        <td className="px-4 py-3 text-slate-755">{row.total}</td>
                        <td className="px-4 py-3 text-emerald-600 font-bold">{row.completed}</td>
                        <td className="px-4 py-3 text-slate-500">{row.avgTime}</td>
                        <td className="px-4 py-3 text-blue-600 font-bold">{row.qcPassRate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="generate" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Case Report Card */}
            <div className="crm-card p-6 flex flex-col justify-between h-96">
              <div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-2">Case-wise Audit Report</h3>
                <p className="text-xs text-slate-555 leading-relaxed mb-4">Compile dynamic verification questionnaires, geographical GPS tagging, and physical image logs for a single case.</p>
                
                {!selectedCaseForReport ? (
                  <div className="space-y-3">
                    <Input
                      type="text"
                      placeholder="Search by Case ID or Applicant..."
                      value={caseSearchQuery}
                      onChange={(e) => setCaseSearchQuery(e.target.value)}
                      className="bg-white border-slate-200 text-slate-800 text-xs"
                    />
                    
                    {caseSearchQuery.length > 2 && (
                      <div className="max-h-36 overflow-y-auto bg-white border border-slate-200 rounded-lg p-1.5 space-y-1">
                        {cases.filter(c => 
                          c.refNumber.toLowerCase().includes(caseSearchQuery.toLowerCase()) || 
                          c.applicantName.toLowerCase().includes(caseSearchQuery.toLowerCase())
                        ).slice(0, 5).map(c => (
                          <div 
                            key={c.id} 
                            className="p-2 hover:bg-slate-50 cursor-pointer rounded text-[11px] flex justify-between border-b border-slate-200 last:border-0" 
                            onClick={() => setSelectedCaseForReport(c)}
                          >
                            <span className="font-semibold text-blue-600">{c.refNumber}</span>
                            <span className="text-slate-500 font-medium">{c.applicantName}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3 p-3 border border-slate-200 rounded-lg bg-slate-50 text-xs">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-slate-800">{selectedCaseForReport.refNumber}</p>
                        <p className="text-slate-500 mt-0.5">{selectedCaseForReport.applicantName}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedCaseForReport(null)} className="h-6 px-2 text-[10px] uppercase font-bold text-slate-500 hover:text-slate-805 hover:bg-slate-100">Change</Button>
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500 font-semibold mb-1 block">Selected Enforced Layout Template</Label>
                      <div className="bg-slate-50 p-2 rounded border border-slate-200 text-[11px] font-semibold text-blue-600">
                        {selectedCaseForReport.client.name} Custom Format
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Button 
                disabled={!selectedCaseForReport} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2 shadow-lg shadow-blue-500/20"
                onClick={() => triggerExportFlow('case')}
              >
                <Download className="w-4 h-4" />
                Configure columns & Download PDF
              </Button>
            </div>

            {/* MIS Export Card */}
            <div className="crm-card p-6 flex flex-col justify-between h-96">
              <div>
                <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-2">MIS Spreadsheets Export</h3>
                <p className="text-xs text-slate-555 leading-relaxed mb-4">Generate bulk spreadsheets mapping client SLAs, FE payouts, QC passes, and active case statuses.</p>
                <div className="space-y-2">
                  <Label className="text-[10px] font-semibold text-slate-500 uppercase">Target Audit Interval</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" className="bg-white border-slate-200 text-slate-800 text-xs" />
                    <Input type="date" className="bg-white border-slate-200 text-slate-800 text-xs" />
                  </div>
                </div>
              </div>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2 shadow-lg shadow-emerald-500/20"
                onClick={() => triggerExportFlow('mis')}
              >
                <Download className="w-4 h-4" />
                Configure columns & Download Excel
              </Button>
            </div>

            {/* Verification Images Export Card */}
            <div className="crm-card p-6 flex flex-col justify-between h-96">
              <div>
                <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center mb-4">
                  <ImageIcon className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-base font-bold text-slate-805 mb-2">ZIP Image Packages</h3>
                <p className="text-xs text-slate-555 leading-relaxed mb-4">Compile geo-tagged field verification photos, landmark visits, and signed documents as compressed ZIP archives.</p>
                <div className="space-y-2">
                  <Label className="text-[10px] font-semibold text-slate-500 uppercase">Target Filter Allocation</Label>
                  <Select defaultValue="all">
                    <SelectTrigger className="bg-white border-slate-200 text-slate-800 text-xs">
                      <SelectValue placeholder="Select Allocation" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-805">
                      <SelectItem value="all">All Available Photos</SelectItem>
                      <SelectItem value="today">Photos Captured Today</SelectItem>
                      <SelectItem value="week">Photos Captured This Week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold gap-2 shadow-lg shadow-amber-500/20"
                onClick={() => triggerExportFlow('images')}
              >
                <Download className="w-4 h-4" />
                Configure details & Compile ZIP
              </Button>
            </div>

            {/* Custom Report Template Upload (Client View Enforced) */}
            {currentRole === ROLES.CLIENT && (
              <div className="crm-card p-6 border-indigo-200 flex flex-col justify-between h-96 bg-indigo-50 border border-indigo-100 animate-fade-in">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-base font-bold text-slate-800 mb-2">Upload Enforced Client Format</h3>
                  <p className="text-xs text-slate-555 leading-relaxed mb-4">Submit your institutional PDF or Excel template format. The AD Ventures QA engine will parse cases into this custom layout automatically.</p>
                  <div className="space-y-2">
                    <Input type="file" accept=".pdf,.xls,.xlsx" className="bg-white border-slate-200 text-slate-805 text-xs" />
                    <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">Supported extensions: PDF, XLS, XLSX (Max file size: 5MB)</p>
                  </div>
                </div>
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold gap-2" onClick={() => alert('Institutional layout submitted! Our QC compliance team will review and map database queries in 24 hours.')}>
                  <Upload className="w-4 h-4" />
                  Upload Format
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="downloads" className="mt-6">
          <div className="crm-card p-6">
            <h3 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2">
              <Download className="w-4 h-4 text-blue-500" />
              Generated Document Archives & Reports
            </h3>
            <div className="space-y-4">
              {[
                { name: 'MIS_Report_SLA_Q1_2026.xlsx', type: 'Excel', size: '2.4 MB', date: '2026-05-20', status: 'ready' },
                { name: 'Case_Report_HDFC_CC_00123.pdf', type: 'PDF', size: '856 KB', date: '2026-05-19', status: 'ready' },
                { name: 'Verification_Images_Paldi_Batch.zip', type: 'ZIP', size: '45.2 MB', date: '2026-05-18', status: 'ready' },
                { name: 'Monthly_Partner_Disbursals_April.pdf', type: 'PDF', size: '1.2 MB', date: '2026-05-10', status: 'ready' },
              ].map((file, index) => {
                const handleDownloadArchive = () => {
                  const blob = new Blob([
                    `AD Ventures Document Archive\nDocument: ${file.name}\nType: ${file.type}\nCompiled Date: ${file.date}\nStatus: VERIFIED AUDIT READY`
                  ], { type: 'text/plain;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = file.name;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success(`Downloaded: ${file.name}`);
                };
                return (
                  <div
                    key={index}
                    onClick={handleDownloadArchive}
                    className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${
                        file.type === 'Excel' 
                          ? 'bg-emerald-50 border-emerald-100 text-emerald-600' 
                          : file.type === 'PDF' 
                            ? 'bg-red-50 border-red-100 text-red-600' 
                            : 'bg-amber-50 border-amber-100 text-amber-600'
                      }`}>
                        {file.type === 'Excel' ? (
                          <FileSpreadsheet className="w-5 h-5" />
                        ) : file.type === 'PDF' ? (
                          <FileText className="w-5 h-5" />
                        ) : (
                          <Package className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-600">{file.name}</p>
                        <p className="text-xs text-slate-500">{file.size} • Compiled on {file.date}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 group-hover:text-blue-600 hover:bg-blue-50 h-9 w-9"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadArchive();
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Pre-Download Dynamic Column Configuration Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="bg-white border border-slate-200 max-w-2xl text-slate-800">
          <DialogHeader className="border-b border-slate-100 pb-3">
            <DialogTitle className="text-slate-900 text-lg flex items-center gap-2">
              <FileDown className="w-5 h-5 text-blue-500" />
              Configure Export Columns Checklist
            </DialogTitle>
            <DialogDescription className="text-slate-555 text-xs">
              Select which database columns and metadata fields to include in your output document.
            </DialogDescription>
          </DialogHeader>

          {isExporting ? (
            <div className="py-8 space-y-4 text-center animate-fade-in">
              <div className="w-12 h-12 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto animate-bounce">
                <FileDown className="w-6 h-6 text-blue-600" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-800">Compiling Report Dataset...</p>
                <p className="text-xs text-slate-500">Injecting selected fields and checking signature hashes</p>
              </div>
              <div className="max-w-xs mx-auto bg-slate-100 h-2 rounded-full overflow-hidden border border-slate-200">
                <div 
                  className="bg-blue-600 h-full rounded-full transition-all duration-150"
                  style={{ width: `${exportProgress}%` }}
                />
              </div>
              <span className="text-xs font-bold text-blue-600">{exportProgress}% Completed</span>
            </div>
          ) : (
            <div className="space-y-4 py-3">
              {/* Modal Controllers */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 font-semibold uppercase">Export Format:</span>
                    <div className="flex bg-slate-100 border border-slate-200 p-0.5 rounded text-xs text-slate-650">
                      {['xlsx', 'csv', 'pdf'].map(fmt => (
                        <button
                          key={fmt}
                          onClick={() => setExportFormat(fmt)}
                          className={`px-3 py-1 rounded text-[10px] uppercase font-bold transition-all ${
                            exportFormat === fmt ? 'bg-blue-600 text-white shadow' : 'hover:text-slate-800'
                          }`}
                        >
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs font-semibold">
                  <button 
                    onClick={handleSelectAll}
                    className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded"
                  >
                    Select All (21)
                  </button>
                  <button 
                    onClick={handleDeselectAll}
                    className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded"
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              {/* Column Filter Search */}
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Filter export columns by name or category (e.g., Timeline, Applicant)..."
                  value={columnSearch}
                  onChange={(e) => setColumnSearch(e.target.value)}
                  className="bg-white border-slate-200 text-slate-800 text-xs pl-9 h-9"
                />
              </div>

              {/* 21 Column Selection Checkbox Grid */}
              <div className="max-h-64 overflow-y-auto pr-1">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {filteredExportColumns.map((col) => {
                    const isChecked = selectedColumns.includes(col.id);
                    return (
                      <div 
                        key={col.id}
                        onClick={() => toggleColumn(col.id)}
                        className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer select-none transition-all ${
                          isChecked 
                            ? 'bg-blue-50 border-blue-200 text-blue-900' 
                            : 'bg-white border-slate-200 text-slate-650 hover:bg-slate-50'
                        }`}
                      >
                        <div className="mt-0.5 flex-shrink-0">
                          {isChecked ? (
                            <CheckSquare className="w-4 h-4 text-blue-500 fill-blue-500/20" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-350" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold truncate leading-tight">{col.label}</p>
                          <span className="text-[9px] text-slate-500 font-semibold tracking-wide uppercase mt-1 block">{col.category}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {filteredExportColumns.length === 0 && (
                  <div className="text-center py-8 text-slate-500 text-xs flex flex-col items-center gap-2">
                    <Info className="w-5 h-5 text-slate-400" />
                    No data fields match your search filter.
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-[10px] text-slate-500 font-semibold leading-relaxed">
                <span>Selected {selectedColumns.length} of 21 Fields Map</span>
                <span className="text-blue-500">Auto-validating integrity compliance rules.</span>
              </div>
            </div>
          )}

          <DialogFooter className="border-t border-slate-200 pt-3">
            <Button 
              variant="outline" 
              onClick={() => setShowExportModal(false)} 
              disabled={isExporting}
              className="border-slate-200 bg-transparent text-slate-605 hover:text-slate-805 hover:bg-slate-50"
            >
              Cancel
            </Button>
            {!isExporting && (
              <Button 
                onClick={startFileCompilation}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2 shadow-lg shadow-blue-500/20"
              >
                <Download className="w-4 h-4" />
                Generate & Export ({selectedColumns.length} Cols)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
